import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Form,
  Input,
  Select,
  Button,
  Divider,
  Alert,
  Space,
  Card,
  Popconfirm,
  Tag
} from "antd";
import { 
  EyeInvisibleOutlined, 
  EyeTwoTone, 
  EditOutlined, 
  DeleteOutlined,
  CheckCircleOutlined
} from "@ant-design/icons";
import openNotification from "components/utils/CustomNotification";
import "../../styles/ServerConfig.css";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

const { Option } = Select;
const beAddress = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

interface ConfigField {
  name: string;
  mandatory: boolean;
  db_key: string;
  type: string;
  method: string;
  category?: string;
  subcategory?: string;
  values?: Record<string, any>;
  disabled?: boolean;
  view_type?: string;
  fieldKey?: string;
}

type ServerConfigFields = Record<string, ConfigField>;

const ServerConfig: React.FC = () => {
  useCheckSessionHeartBeat();
  const location = useLocation();
  const navigate = useNavigate();
  const orgDetailsFromNav = location.state?.orgDetails;
  const initialMode: "add" | "edit" | "view" = location.state?.mode || "add";

  const [mode, setMode] = useState<"add" | "edit" | "view">(initialMode);
  const [orgDetails, setOrgDetails] = useState<any>(orgDetailsFromNav);
  const [serverConfigFields, setServerConfigFields] = useState<ServerConfigFields>({});
  const [serverFormData, setServerFormData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [sessionUid, setSessionUid] = useState("");
  const [userUid, setUserUid] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  
  // DB Validation States
  const [dbValidated, setDbValidated] = useState(false);
  const [dbAdded, setDbAdded] = useState(false);
  const [dbEnabled, setDbEnabled] = useState(false);
  const [dbInitialized, setDbInitialized] = useState(false);
  const [dbValidationLoading, setDbValidationLoading] = useState(false);
  const [dbModified, setDbModified] = useState(false);
  
  // Validation States
  const [orgUidValidation, setOrgUidValidation] = useState({ status: "", message: "" });
  const [privilege, setPrivilege] = useState("");
  const [userValidation, setUserValidation] = useState({ status: "", message: "" });
  const [pendingEnable, setPendingEnable] = useState(false);
  const [pendingInitialize, setPendingInitialize] = useState(false);

  // Email Config States
  const [emailConfigs, setEmailConfigs] = useState<Record<string, any>>({});
  const emailConfigsRef = useRef<Record<string, any>>({});
  const [activeEmailConfig, setActiveEmailConfig] = useState<string | null>(null);
  const [editingEmailConfigs, setEditingEmailConfigs] = useState<Record<string, any>>({});
  const [emailFormData, setEmailFormData] = useState<Record<string, any>>({});
  const [emailValidationErrors, setEmailValidationErrors] = useState<Record<string, string>>({});
  
  const [smtpValidated, setSmtpValidated] = useState(false);
  const [smtpValidationLoading, setSmtpValidationLoading] = useState(false);
  const [smtpFieldsModified, setSmtpFieldsModified] = useState(false);
  
  const [graphValidated, setGraphValidated] = useState(false);
  const [graphValidationLoading, setGraphValidationLoading] = useState(false);
  const [graphFieldsModified, setGraphFieldsModified] = useState(false);

  useEffect(() => {
    const storedSession = localStorage.getItem("session_uid") || "";
    const storedUser = localStorage.getItem("user_uid") || "";
    const storedPrivilege = localStorage.getItem("privilege") || "";
    setSessionUid(storedSession);
    setUserUid(storedUser);
    setPrivilege(storedPrivilege);
  }, []);

  useEffect(() => {
    if (privilege === "orgadmin") {
      setMode("view");
      if (sessionUid && userUid) getOrgConfigs();
    }
  }, [privilege, sessionUid, userUid]);

  useEffect(() => {
    if (sessionUid && userUid) {
      getOrgConfigFieldsFilter().then(async () => {
        if (orgDetailsFromNav?.org_uid) {
          await getOrgConfigs();
          setMode("view");
        } else if (mode === "edit" || mode === "view") {
          await getOrgConfigs();
        }
      });
    }
  }, [sessionUid, userUid]);

  useEffect(() => {
    if (Object.keys(serverConfigFields).length === 0) return;
    const initialFormData: Record<string, any> = {};
    Object.entries(serverConfigFields).forEach(([_, cfg]) => {
      // Exclude email categories from main server data
      if (['email', 'smtp', 'graph'].includes(normalizeEmailType(cfg.category))) return;
      
      const key = cfg.category ? `${cfg.category}_${cfg.db_key}` : cfg.db_key;
      if (mode === "add") {
        initialFormData[key] = "";
      } else {
        if (cfg.category === "db") initialFormData[key] = orgDetails?.db?.[cfg.db_key] ?? "";
        else initialFormData[key] = orgDetails?.[cfg.db_key] ?? "";
      }
    });
    setServerFormData(initialFormData);
    loadEmailConfigsFromDetails();
  }, [serverConfigFields, orgDetails, mode]);

  const loadEmailConfigsFromDetails = () => {
    if (!orgDetails && mode !== "edit" && mode !== "view") return;

    const configs: Record<string, any> = {};
    let activeConfig = null;

    if (orgDetails?.email) {
      activeConfig = normalizeEmailType(orgDetails.email.type);
      if (orgDetails.email.smtp) configs.smtp = orgDetails.email.smtp;
      if (orgDetails.email.graph) configs.graph = orgDetails.email.graph;
    } else {
      if (orgDetails?.smtp) configs.smtp = orgDetails.smtp;
      if (orgDetails?.graph) configs.graph = orgDetails.graph;
    }

    if (Object.keys(configs).length > 0) {
      setEmailConfigs(configs);
      emailConfigsRef.current = configs;
      if (activeConfig) setActiveEmailConfig(activeConfig);
      else if (configs.smtp) setActiveEmailConfig('smtp');
      else if (configs.graph) setActiveEmailConfig('graph');
    }
  };

  const validateOrgUid = async (uid: string) => {
    if (!uid) {
      setOrgUidValidation({ status: "error", message: "Org UID cannot be empty." });
      return;
    }
    setOrgUidValidation({ status: "checking", message: "" });
    try {
      const res = await axios.post(`${beAddress}/api/validate-org`, uid, {
        headers: { "user-uid": userUid, "Content-Type": "application/json" },
        withCredentials: true,
      });
      if (res?.data?.status === "Success") {
        setOrgUidValidation({ status: "success", message: "Org UID is valid." });
      } else {
        setOrgUidValidation({ status: "error", message: res?.data?.data || "Invalid Org UID." });
      }
    } catch (err: any) {
      setOrgUidValidation({ status: "error", message: err.response?.data?.data || "Validation failed." });
    }
  };

  const validateUser = async (userValue: string) => {
    if (!userValue) {
      setUserValidation({ status: "error", message: "User cannot be empty." });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userValue)) {
      setUserValidation({ status: "error", message: "Please enter a valid email address." });
      return;
    }
    setUserValidation({ status: "checking", message: "" });

    try {
      const payload = { target_org_uid: serverFormData["org_uid"] || "", target_user: userValue };
      const res = await axios.post(`${beAddress}/api/validate-org-user`, payload, {
        headers: { "user-uid": userUid, "Content-Type": "application/json" },
        withCredentials: true,
      });

      if (res?.data?.status === "Success") {
        setUserValidation({ status: "success", message: "User is valid." });
      } else {
        setUserValidation({ status: "error", message: res?.data?.data || "Invalid user." });
      }
    } catch (err: any) {
      setUserValidation({ status: "error", message: err.response?.data?.data || "User validation failed." });
    }
  };

  const getOrgConfigFieldsFilter = async () => {
    if (!sessionUid || !userUid) return;
    try {
      setLoading(true);
      const res = await axios.get(`${beAddress}/api/org-config-filter`, {
        headers: { "user-uid": userUid },
        withCredentials: true,
      });
      if (res.data?.status === "Success") {
        const rawData: ServerConfigFields = res.data.data;
        const normalized: ServerConfigFields = {};
        Object.entries(rawData).forEach(([key, cfg]) => {
          normalized[key] = { ...(cfg as ConfigField), fieldKey: key };
          // Apply secure type for graph secrets
          if (cfg.category === 'graph' && (cfg.db_key === 'client_secret' || cfg.db_key === 'client_id')) {
            normalized[key].view_type = 'secure';
          }
        });
        setServerConfigFields(normalized);
      } else {
        openNotification({ type: "error", message: res.data?.message || "Failed to fetch fields" });
      }
    } catch (err: any) {
      openNotification({ type: "error", message: err.response?.data?.message || "Failed to fetch fields" });
    } finally {
      setLoading(false);
    }
  };

  const getOrgConfigs = async () => {
    if (!sessionUid || !userUid) return;
    try {
      setLoading(true);
      let orgUid = orgDetailsFromNav?.org_uid;
      if (privilege === "orgadmin" && !orgUid) orgUid = localStorage.getItem("org_uid");
      if (!orgUid) {
        setLoading(false);
        return;
      }
      const res = await axios.get(`${beAddress}/api/org-configs`, {
        headers: { "user-uid": userUid },
        withCredentials: true,
        params: { org_uid: orgUid },
      });
      if (res.data?.status === "Success") {
        const org = res.data.data?.[0];
        setOrgDetails(org);
        setDbInitialized(org?.db?.initialized || false);
        setDbEnabled(org?.db?.enabled || false);
        setDbAdded(!!org?.db);
      } else {
        openNotification({ type: "error", message: res.data?.message || "Failed to fetch org configs" });
      }
    } catch (err: any) {
      openNotification({ type: "error", message: err.response?.data?.message || "Failed to fetch org configs" });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key: string, value: any, category?: string) => {
    if (mode === "view") return;
    const finalKey = category ? `${category}_${key}` : key;
    setServerFormData({ ...serverFormData, [finalKey]: value });
    if (mode === "edit" && category === "db") {
      const originalValue = orgDetails?.db?.[key] ?? "";
      if (value !== originalValue) setDbModified(true);
      else {
        const anyModified = Object.values(serverConfigFields)
          .filter((f) => f.category === "db")
          .some((f) => serverFormData[`${f.category}_${f.db_key}`] !== orgDetails?.db?.[f.db_key]);
        setDbModified(anyModified);
      }
    }
    if (mode === "add" && category === "db" && dbValidated) {
      setDbModified(true);
      setDbValidated(false);
    }
  };

  // --- EMAIL LOGIC ---
  const normalizeEmailType = (value: string | null | undefined) => (value ?? "").toString().toLowerCase();

  const getEmailEnableFieldKey = (type: string) => {
    const normalizedType = normalizeEmailType(type);
    const entry = Object.entries(serverConfigFields).find(
      ([, config]) => normalizeEmailType(config.category) === normalizedType && config.db_key === "enable"
    );
    return entry ? entry[0] : null;
  };

  const handleEmailFormChange = (key: string, value: any, type: string) => {
    setEmailFormData(prev => ({ ...prev, [key]: value }));
    const normalizedType = normalizeEmailType(type);
    if (normalizedType === 'smtp') setSmtpFieldsModified(true);
    else if (normalizedType === 'graph') setGraphFieldsModified(true);
  };

  const ensureSmtpSecurityFileKeys = (target: any) => {
    if (!target.security) target.security = {};
    ["cert_file", "ca_file", "key_file"].forEach((key) => {
      if (target.security[key] == null || target.security[key] === "") {
        target.security[key] = "";
      }
    });
  };

  const buildSmtpPayloadConfig = (smtpConfig: any, activeEmailType: string | null) => {
    const normalized = {
      ...smtpConfig,
      enable: activeEmailType === "smtp" ? "Yes" : "No",
      type: "smtp",
    };
    ensureSmtpSecurityFileKeys(normalized);
    return normalized;
  };

  const applyEmailEnableState = (configs: Record<string, any>, activeType: string | null) => {
    if (!activeType) return configs;
    const updated: Record<string, any> = {};
    Object.entries(configs).forEach(([type, cfg]) => {
      updated[type] = { ...cfg, enable: type === activeType ? "Yes" : "No" };
    });
    return updated;
  };

  const persistValidatedEmailConfig = (normalizedType: string) => {
    const fieldsForType = Object.entries(serverConfigFields).filter(
      ([, config]) => normalizeEmailType(config.category) === normalizedType
    );

    const transformedData: any = {};
    fieldsForType.forEach(([key, config]) => {
      const formKey = config.fieldKey!;
      const dbKey = config.db_key;
      let value = emailFormData[formKey];

      if (value !== undefined) {
        if (config.type === "int" && value !== "") value = parseInt(value, 10);
        if (config.subcategory === "security") {
          if (!transformedData.security) transformedData.security = {};
          transformedData.security[dbKey] = value;
        } else {
          transformedData[dbKey] = value;
        }
      }
    });

    if (normalizedType === "smtp") ensureSmtpSecurityFileKeys(transformedData);

    const isNew = editingEmailConfigs?.[normalizedType]?.isNew === true;
    const nextActiveType = isNew ? normalizedType : normalizeEmailType(activeEmailConfig) || normalizedType;

    const newEmailConfigs = { ...emailConfigs, [normalizedType]: transformedData };
    const updatedEmailConfigs = applyEmailEnableState(newEmailConfigs, nextActiveType);

    emailConfigsRef.current = updatedEmailConfigs;
    setEmailConfigs(updatedEmailConfigs);
    setActiveEmailConfig(nextActiveType);
    handleCancelEditEmailConfig(normalizedType, false);
    setEmailValidationErrors({});
  };

  const handleValidateEmailConfig = async (type: string) => {
    const normalizedType = normalizeEmailType(type);
    const fieldsForType = Object.entries(serverConfigFields).filter(
      ([, config]) => normalizeEmailType(config.category) === normalizedType
    );

    const errors: Record<string, string> = {};
    fieldsForType.forEach(([key, config]) => {
      if (config.mandatory) {
        const value = emailFormData[key];
        let isMandatory = true;
        
        const parentField = Object.values(serverConfigFields).find(
          p => p.method === 'dropdown' && normalizeEmailType(p.category) === normalizedType && Object.values(p.values || {}).flat().includes(key)
        );

        if (parentField) {
          const parentValue = emailFormData[parentField.fieldKey!];
          const visibleChildren = parentField.values?.[parentValue] || [];
          if (!visibleChildren.includes(key)) isMandatory = false;
        }

        if (isMandatory && (!value || String(value).trim() === '')) {
          errors[key] = `${config.name} is required.`;
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setEmailValidationErrors(errors);
      openNotification({ type: "error", message: "Please fill all mandatory fields for validation." });
      return;
    }
    
    setEmailValidationErrors({});
    if (normalizedType === 'smtp') setSmtpValidationLoading(true);
    else if (normalizedType === 'graph') setGraphValidationLoading(true);

    const payload: any = {};
    fieldsForType.forEach(([, config]) => {
      const formKey = config.fieldKey!;
      const dbKey = config.db_key;
      let value = emailFormData[formKey];

      if (value !== undefined) {
        if (config.type === "int" && value !== "") value = parseInt(value, 10);
        if (config.subcategory === 'security') {
          if (!payload.security) payload.security = {};
          payload.security[dbKey] = value;
        } else {
          payload[dbKey] = value;
        }
      }
    });

    if (normalizedType === "smtp") ensureSmtpSecurityFileKeys(payload);
    payload.type = normalizedType;

    try {
      const endpoint = normalizedType === 'smtp' ? 'smtp-connection' : 'graph-connection';
      const response = await axios.post(`${beAddress}/api/${endpoint}`, payload, {
        headers: { 'user-uid': userUid }, withCredentials: true,
      });

      if (response?.data?.status === "Success") {
        openNotification({ type: 'success', message: response?.data?.message || `${normalizedType.toUpperCase()} validated successfully` });
        if (normalizedType === 'smtp') {
          setSmtpValidated(true); setSmtpFieldsModified(false);
        } else {
          setGraphValidated(true); setGraphFieldsModified(false);
        }
        persistValidatedEmailConfig(normalizedType);
      } else {
        openNotification({ type: 'error', message: response?.data?.data || response?.data?.message || `${normalizedType.toUpperCase()} validation failed` });
      }
    } catch (err: any) {
      openNotification({ type: 'error', message: err.response?.data?.data || err.response?.data?.message || err.message || `Failed to validate ${normalizedType}` });
    } finally {
      if (normalizedType === 'smtp') setSmtpValidationLoading(false);
      else if (normalizedType === 'graph') setGraphValidationLoading(false);
    }
  };

  const getEmailFormResetByType = (normalizedType: string) => {
    const resetData: Record<string, any> = {};
    Object.entries(serverConfigFields).forEach(([fieldKey, config]) => {
      if (normalizeEmailType(config.category) === normalizedType) resetData[fieldKey] = "";
    });
    return resetData;
  };

  const handleEditEmailConfig = (type: string) => {
    const normalizedType = normalizeEmailType(type);
    setEditingEmailConfigs(prev => ({ ...prev, [normalizedType]: { type: normalizedType, isNew: false } }));

    const dbData = emailConfigs[type] || emailConfigs[normalizedType] || {};
    const formData: Record<string, any> = {};

    if (dbData && typeof dbData === 'object') {
      const fieldsForType = Object.entries(serverConfigFields).filter(
        ([, config]) => normalizeEmailType(config.category) === normalizedType
      );

      fieldsForType.forEach(([fieldKey, config]) => {
        let value;
        if (config.subcategory === 'security' && dbData.security) value = dbData.security[config.db_key];
        else value = dbData[config.db_key];
        if (value !== undefined) formData[fieldKey] = value;
      });
    }

    setEmailFormData(prev => ({ ...prev, ...formData }));
    if (normalizedType === 'smtp') { setSmtpValidated(true); setSmtpFieldsModified(false); }
    else if (normalizedType === 'graph') { setGraphValidated(true); setGraphFieldsModified(false); }
  };

  const handleAddNewEmailConfig = (type: string) => {
    const normalizedType = normalizeEmailType(type);
    setEditingEmailConfigs(prev => ({ ...prev, [normalizedType]: { type: normalizedType, isNew: true } }));

    const enableKey = getEmailEnableFieldKey(normalizedType);
    const resetData = getEmailFormResetByType(normalizedType);

    setEmailFormData(prev => ({
      ...prev,
      ...resetData,
      ...(enableKey ? { [enableKey]: "Yes" } : {})
    }));

    if (normalizedType === 'smtp') { setSmtpValidated(false); setSmtpFieldsModified(false); }
    else if (normalizedType === 'graph') { setGraphValidated(false); setGraphFieldsModified(false); }
  };

  const handleCancelEditEmailConfig = (type: string, resetValidation = true) => {
    if (!type) return;
    const normalizedType = normalizeEmailType(type);
    setEditingEmailConfigs(prev => {
      const next = { ...prev };
      delete next[normalizedType];
      return next;
    });

    if (normalizedType === 'smtp') {
      setSmtpFieldsModified(false);
      if (resetValidation) setSmtpValidated(false);
    } else if (normalizedType === 'graph') {
      setGraphFieldsModified(false);
      if (resetValidation) setGraphValidated(false);
    }
  };

  const handleDeleteEmailConfig = (type: string) => {
    const normalizedType = normalizeEmailType(type);
    const newEmailConfigs = { ...emailConfigs };
    delete newEmailConfigs[normalizedType];

    const resetData = getEmailFormResetByType(normalizedType);
    setEmailFormData(prev => ({ ...prev, ...resetData }));

    if (normalizeEmailType(activeEmailConfig) === normalizedType) {
      const remainingConfigs = Object.keys(newEmailConfigs);
      if (remainingConfigs.length > 0) {
        const nextActiveType = remainingConfigs[0];
        const updatedEmailConfigs = applyEmailEnableState(newEmailConfigs, nextActiveType);
        setEmailConfigs(updatedEmailConfigs);
        emailConfigsRef.current = updatedEmailConfigs;
        setActiveEmailConfig(nextActiveType);
      } else {
        setEmailConfigs(newEmailConfigs);
        emailConfigsRef.current = newEmailConfigs;
        setActiveEmailConfig(null);
      }
    } else {
      setEmailConfigs(newEmailConfigs);
      emailConfigsRef.current = newEmailConfigs;
    }
  };

  // --- SAVE & ACTIONS ---
  const buildDbPayload = () => {
    const dbPayload: Record<string, any> = {};
    Object.values(serverConfigFields)
      .filter((f) => f.category === "db")
      .forEach((f) => {
        const key = `${f.category}_${f.db_key}`;
        dbPayload[f.db_key] = f.db_key === "port" ? Number(serverFormData[key]) : (serverFormData[key] ?? "");
      });
    if (!dbPayload.type && serverFormData["db_type"]) dbPayload.type = serverFormData["db_type"];
    return dbPayload;
  };

  const getFullPayload = () => {
    const payload: any = {
      db: buildDbPayload(),
      ...serverFormData,
      enabled: orgDetails?.enabled ?? false,
      initialized: orgDetails?.initialized ?? false,
    };

    const currentEmailConfigs = emailConfigsRef.current;
    if (activeEmailConfig) {
      const emailPayload: any = { type: activeEmailConfig };
      if (currentEmailConfigs.smtp) {
        emailPayload.smtp = buildSmtpPayloadConfig(currentEmailConfigs.smtp, activeEmailConfig);
      }
      if (currentEmailConfigs.graph) {
        emailPayload.graph = {
          ...currentEmailConfigs.graph,
          enable: activeEmailConfig === 'graph' ? 'Yes' : 'No',
          type: 'graph'
        };
      }
      payload.email = emailPayload;
    }
    return payload;
  };

  const handleSave = async () => {
    if (Object.keys(editingEmailConfigs).length > 0) {
      openNotification({ type: "warning", message: "Please save or cancel email configurations before proceeding." });
      return;
    }

    setSubmitLoading(true);
    try {
      const res = await axios.put(`${beAddress}/api/org-db`, getFullPayload(), {
        headers: { "user-uid": userUid },
        withCredentials: true,
      });
      if (res.data.status === "Success") {
        openNotification({ type: "success", message: res.data.message || "Configuration updated successfully" });
        await getOrgConfigs();
        setMode("view");
        setDbModified(false);
        if (!orgDetails?.enabled && !orgDetails?.initialized) {
          setPendingEnable(true); 
        }
      } else {
        openNotification({ type: "error", message: res.data.message || "Failed to save configuration" });
      }
    } catch (err: any) {
      openNotification({ type: "error", message: err.response?.data?.message || "Failed to save configuration" });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    setMode("view");
    const resetData: Record<string, any> = {};
    Object.entries(serverConfigFields).forEach(([_, cfg]) => {
      if (['email', 'smtp', 'graph'].includes(normalizeEmailType(cfg.category))) return;
      const key = cfg.category ? `${cfg.category}_${cfg.db_key}` : cfg.db_key;
      if (cfg.category === "db") resetData[key] = orgDetails?.db?.[cfg.db_key] ?? "";
      else resetData[key] = orgDetails?.[cfg.db_key] ?? "";
    });
    setServerFormData(resetData);
    setDbModified(false);
    setEditingEmailConfigs({});
    loadEmailConfigsFromDetails();
  };

  const handleValidateDb = async () => {
    if (mode === "add") {
      const missingFields = Object.values(serverConfigFields)
        .filter((f) => f.mandatory && f.category === 'db')
        .map((f) => `${f.category}_${f.db_key}`)
        .filter((key) => !serverFormData[key] || serverFormData[key].toString().trim() === "");
        
      if (missingFields.length > 0) {
        openNotification({ type: "error", message: "Please fill all mandatory database fields." });
        return;
      }
    }

    setDbValidationLoading(true);
    try {
      const dbPayload = {
        db_type: serverFormData["db_type"] || "",
        host: serverFormData["db_host"] || "",
        port: Number(serverFormData["db_port"]) || 0,
        user: serverFormData["db_user"] || "",
        password: serverFormData["db_password"] || "",
      };
      const res = await axios.post(`${beAddress}/api/db-connection`, dbPayload, {
        headers: { "user-uid": userUid },
        withCredentials: true,
      });
      if (res.data.status === "Success") {
        setDbValidated(true);
        setDbModified(false);
        openNotification({ type: "success", message: "Database connection validated successfully." });
      } else {
        openNotification({ type: "error", message: res.data.message || "DB validation failed." });
      }
    } catch (err: any) {
      openNotification({ type: "error", message: err.response?.data?.message || "DB validation failed." });
    } finally {
      setDbValidationLoading(false);
    }
  };

  const handleAddDb = async () => {
    if (Object.keys(editingEmailConfigs).length > 0 || !activeEmailConfig) {
      openNotification({ type: "warning", message: "Please finalize an email configuration first." });
      return;
    }
    setSubmitLoading(true);
    try {
      const payload = getFullPayload();
      const res = await axios.post(`${beAddress}/api/org-db`, payload, {
        headers: { "user-uid": userUid },
        withCredentials: true,
      });
      if (res.data.status === "Success") {
        setDbAdded(true);
        openNotification({ type: "success", message: "Database record added successfully." });
      } else {
        openNotification({ type: "error", message: res.data.message || "Failed to add DB record." });
      }
    } catch (err: any) {
      openNotification({ type: "error", message: err.response?.data?.message || "Failed to add configuration." });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEnableDb = async () => {
    if (Object.keys(editingEmailConfigs).length > 0) return;
    setSubmitLoading(true);
    try {
      const payload = getFullPayload();
      payload.enabled = true;
      const res = await axios.put(`${beAddress}/api/org-db`, payload, {
        headers: { "user-uid": userUid },
        withCredentials: true,
      });
      if (res.data.status === "Success") {
        setDbEnabled(true);
        await getOrgConfigs();
        openNotification({ type: "success", message: "Database enabled successfully." });
        if (!orgDetails?.initialized) {
          setPendingEnable(false);
          setPendingInitialize(true);
        }
      } else {
        openNotification({ type: "error", message: res.data.message || "Failed to enable database." });
      }
    } catch (err: any) {
      openNotification({ type: "error", message: err.response?.data?.message || "Failed to enable database." });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleInitializeDb = async () => {
    if (Object.keys(editingEmailConfigs).length > 0) return;
    setSubmitLoading(true);
    try {
      const payload = getFullPayload();
      payload.enabled = true;
      const res = await axios.post(`${beAddress}/api/org-db-initialize`, payload, { 
        headers: { "user-uid": userUid }, 
        withCredentials: true 
      });
      if (res.data.status === "Success") {
        setDbInitialized(true);
        openNotification({ type: "success", message: "Database initialized successfully." });
        await getOrgConfigs();
        navigate("/org-server-configuration", { state: { orgDetails } });
      } else {
        openNotification({ type: "error", message: res.data.message || "Initialization failed." });
      }
    } catch (err: any) {
      openNotification({ type: "error", message: err.response?.data?.message || "Failed to initialize database." });
    } finally {
      setSubmitLoading(false);
    }
  };

  // --- RENDERERS ---
  const renderServerField = (key: string, cfg: ConfigField) => {
    if (['email', 'smtp', 'graph'].includes(normalizeEmailType(cfg.category))) return null;
    
    const fieldKey = cfg.category ? `${cfg.category}_${cfg.db_key}` : cfg.db_key;
    const value = serverFormData[fieldKey];
    const disabled = mode === "view" || cfg.disabled || (cfg.db_key === "org_uid" && mode === "edit");
    const placeholderText = mode === "add" ? `Enter ${cfg.name.toLowerCase()}` : undefined;

    const labelStyle = { color: "var(--text-color)" };
    const inputStyle = { color: "var(--text-color)" };

    if (cfg.method === "input") {
      if (cfg.view_type === "secure") {
        return (
          <Form.Item key={key} label={<span style={labelStyle}>{cfg.name}</span>} required={cfg.mandatory}>
            <Input.Password
              value={value}
              disabled={disabled}
              placeholder={placeholderText}
              onChange={(e) => handleChange(cfg.db_key, e.target.value, cfg.category)}
              iconRender={(visible) => visible ? <EyeTwoTone style={inputStyle} /> : <EyeInvisibleOutlined style={inputStyle} />}
              style={inputStyle}
            />
          </Form.Item>
        );
      }
      return (
        <Form.Item key={key} label={<span style={labelStyle}>{cfg.name}</span>} required={cfg.mandatory}>
          <Input
            value={value}
            disabled={disabled}
            placeholder={placeholderText}
            type={cfg.type === "int" ? "number" : "text"}
            onChange={(e) => {
              handleChange(cfg.db_key, e.target.value, cfg.category);
              if (!cfg.category) {
                if (cfg.db_key === "org_uid") validateOrgUid(e.target.value);
                else if (cfg.db_key === "user") validateUser(e.target.value);
              }
            }}
            style={inputStyle}
          />
          {!cfg.category && cfg.db_key === "org_uid" && orgUidValidation.status && (
            <div className={`org-uid-status ${orgUidValidation.status}`}>
              {orgUidValidation.status === "checking" ? "Checking Org UID..." : orgUidValidation.message}
            </div>
          )}
          {!cfg.category && cfg.db_key === "user" && userValidation.status && (
            <div className={`user-status ${userValidation.status}`}>
              {userValidation.status === "checking" ? "Checking User..." : userValidation.message}
            </div>
          )}
        </Form.Item>
      );
    }

    if (cfg.method === "dropdown") {
      return (
        <Form.Item key={key} label={<span style={labelStyle}>{cfg.name}</span>} required={cfg.mandatory}>
          <Select
            value={value || undefined}
            disabled={disabled}
            onChange={(v) => handleChange(cfg.db_key, v, cfg.category)}
            placeholder={mode === "add" ? `Select ${cfg.name}` : undefined}
            style={inputStyle}
            dropdownStyle={{ color: "var(--text-color)" }}
            popupClassName="custom-select-dropdown"
          >
            {cfg.values && Object.keys(cfg.values).map((v) => (
              <Option key={v} value={v} style={{ color: "var(--text-color)" }}>{v}</Option>
            ))}
          </Select>
        </Form.Item>
      );
    }
    return null;
  };

  const renderEmailField = (key: string, config: ConfigField, type: string) => {
    if (!config || mode === "view") return null;
    if (config.db_key === "enable" && config.subcategory !== "security") return null;

    const value = emailFormData[key];
    const hasError = !!emailValidationErrors[key];

    if (config.method === "dropdown") {
      return (
        <Form.Item 
          key={key} 
          label={<span style={{ color: "var(--text-color)" }}>{config.name}</span>} 
          required={config.mandatory}
          validateStatus={hasError ? "error" : ""}
          help={hasError ? emailValidationErrors[key] : null}
        >
          <Select 
            value={value || undefined} 
            onChange={(v) => handleEmailFormChange(key, v, type)}
            placeholder={`Select ${config.name}`}
            popupClassName="custom-select-dropdown"
          >
            {config.values && Object.keys(config.values).map(opt => (
              <Option key={opt} value={opt}>{opt}</Option>
            ))}
          </Select>
        </Form.Item>
      );
    }

    if (config.method === "input") {
      if (config.view_type === 'secure') {
        return (
          <Form.Item 
            key={key} 
            label={<span style={{ color: "var(--text-color)" }}>{config.name}</span>} 
            required={config.mandatory}
            validateStatus={hasError ? "error" : ""}
            help={hasError ? emailValidationErrors[key] : null}
          >
            <Input.Password
              value={value || ''}
              onChange={(e) => handleEmailFormChange(key, e.target.value, type)}
              placeholder={`Enter ${config.name}`}
              iconRender={visible => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
            />
          </Form.Item>
        );
      }
      return (
        <Form.Item 
          key={key} 
          label={<span style={{ color: "var(--text-color)" }}>{config.name}</span>} 
          required={config.mandatory}
          validateStatus={hasError ? "error" : ""}
          help={hasError ? emailValidationErrors[key] : null}
        >
          <Input 
            type={config.type === "int" ? "number" : "text"}
            value={value || ''}
            onChange={(e) => handleEmailFormChange(key, e.target.value, type)}
            placeholder={`Enter ${config.name}`}
          />
        </Form.Item>
      );
    }
    return null;
  };

  const renderEmailConfigSection = () => {
    // Only show email configs if DB is validated or we are in view mode
    if ((mode === "add" || mode === "edit") && !dbValidated && dbModified) return null;

    const existingTypes = Object.keys(emailConfigs).map(normalizeEmailType);
    const newTypes = Object.keys(editingEmailConfigs).filter(type => editingEmailConfigs[type].isNew);
    const allDisplayTypes = [...new Set([...existingTypes, ...newTypes])];
    const canDeleteEmailConfig = existingTypes.length > 1;

    const availableTypes = ['smtp', 'graph'];
    const typesToAdd = availableTypes.filter(t => !allDisplayTypes.includes(t));

    return (
      <div className="email-config-wrapper">
        <Divider style={{ gridColumn: "1 / -1", color: "var(--text-color)", borderColor: "rgba(0,0,0,0.1)" }}>
          Email Configuration
        </Divider>

        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {allDisplayTypes.map(type => {
            const normalizedType = normalizeEmailType(type);
            const isEditing = mode !== "view" && !!editingEmailConfigs[normalizedType];
            const isNew = editingEmailConfigs[normalizedType]?.isNew;
            const isActive = normalizeEmailType(activeEmailConfig) === normalizedType;

            return (
              <React.Fragment key={type}>
                {/* Summary Card */}
                {!isNew && (
                  <Card 
                    size="small" 
                    className={`email-summary-card ${isActive ? 'active-card' : ''}`}
                  >
                    <span className="email-summary-title" style={{ fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-color)' }}>
                      {type}
                    </span>
                    <Space className="email-summary-actions" size="middle">
                      {isActive ? (
                        <Tag color="success" icon={<CheckCircleOutlined />}>Active</Tag>
                      ) : (
                        mode !== "view" && (
                          <Button 
                            size="small" 
                            type="dashed"
                            onClick={() => {
                              const updated = applyEmailEnableState(emailConfigs, normalizedType);
                              emailConfigsRef.current = updated;
                              setEmailConfigs(updated);
                              setActiveEmailConfig(normalizedType);
                            }}
                          >
                            Set Active
                          </Button>
                        )
                      )}
                      
                      {mode !== "view" && (
                        <>
                          <Button type="text" icon={<EditOutlined style={{ color: "#1890ff" }}/>} onClick={() => handleEditEmailConfig(normalizedType)} />
                          {canDeleteEmailConfig && (
                            <Popconfirm
                              title="Delete Email Configuration"
                              description={`Are you sure you want to delete ${normalizedType.toUpperCase()}?`}
                              onConfirm={() => handleDeleteEmailConfig(normalizedType)}
                              okText="Yes"
                              cancelText="No"
                            >
                              <Button type="text" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                          )}
                        </>
                      )}
                    </Space>
                  </Card>
                )}

                {/* Edit/Add Form */}
                {isEditing && (
                  <Card className="email-edit-card" size="small">
                    <div style={{ marginBottom: 16, fontWeight: 600, textTransform: 'uppercase', color: "var(--text-color)" }}>
                      {isNew ? 'New' : 'Edit'} {normalizedType} Configuration
                    </div>
                    
                    <div className="server-grid">
                      {(() => {
                        const entries = Object.entries(serverConfigFields).filter(
                          ([, config]) => normalizeEmailType(config.category) === normalizedType
                        );

                        if (normalizedType === "graph") {
                          entries.sort(([, a], [, b]) => {
                            const aIsTenant = a.db_key === "tenant_id";
                            const bIsTenant = b.db_key === "tenant_id";
                            if (aIsTenant === bIsTenant) return 0;
                            return aIsTenant ? -1 : 1;
                          });
                        }

                        return entries.flatMap(([key, config]) => {
                          if (config.method === 'dropdown' && config.values) {
                            const selected = emailFormData[key];
                            const extraFields = config.values[selected] || [];
                            return [
                              renderEmailField(key, config, normalizedType),
                              ...extraFields.map((childKey: string) => renderEmailField(childKey, serverConfigFields[childKey], normalizedType))
                            ];
                          }
                          const isChildField = Object.values(serverConfigFields).some(
                            p => p.method === 'dropdown' && normalizeEmailType(p.category) === normalizedType && Object.values(p.values || {}).flat().includes(key)
                          );
                          if (isChildField) return [];

                          return [renderEmailField(key, config, normalizedType)];
                        });
                      })()}
                    </div>

                    {/* Action buttons inside form */}
                    {(() => {
                      const isLoading = (normalizedType === 'smtp' && smtpValidationLoading) || (normalizedType === 'graph' && graphValidationLoading);
                      const isValidated = (normalizedType === 'smtp' && smtpValidated) || (normalizedType === 'graph' && graphValidated);
                      const isModified = (normalizedType === 'smtp' && smtpFieldsModified) || (normalizedType === 'graph' && graphFieldsModified);
                      
                      const showWarning = isValidated && isModified;
                      const showValidateBtn = !isValidated || isModified;

                      return (
                        <>
                          {showWarning && (
                            <Alert 
                              message="Configuration changed. Please re-validate the connection." 
                              type="warning" 
                              showIcon 
                              style={{ marginBottom: 16 }}
                            />
                          )}
                          <Space style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                            {showValidateBtn && (
                              <Button type="primary" className="custom-button" loading={isLoading} onClick={() => handleValidateEmailConfig(normalizedType)}>
                                Validate
                              </Button>
                            )}
                            <Button danger onClick={() => handleCancelEditEmailConfig(normalizedType)}>
                              Cancel
                            </Button>
                          </Space>
                        </>
                      );
                    })()}
                  </Card>
                )}
              </React.Fragment>
            );
          })}

          {/* Add New Email Config Dropdown */}
          {mode !== "view" && typesToAdd.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: "var(--text-color)" }}>Add Email Configuration</div>
              <Select
                value={undefined}
                placeholder="Choose an Email Configuration"
                style={{ width: 250 }}
                onChange={(v) => v && handleAddNewEmailConfig(v)}
                popupClassName="custom-select-dropdown"
              >
                {typesToAdd.map(type => (
                  <Option key={type} value={type}>{type.toUpperCase()}</Option>
                ))}
              </Select>
            </div>
          )}
        </Space>
      </div>
    );
  };

  const renderDbActionButton = () => {
    const isAnyEmailBeingEdited = Object.keys(editingEmailConfigs).length > 0;

    // PENDING ENABLE
    if (privilege === "systemadmin" && pendingEnable && !dbEnabled && !dbInitialized) {
      if (isAnyEmailBeingEdited) return null;
      return <Button type="primary" onClick={handleEnableDb} loading={submitLoading} className="custom-button">Enable</Button>;
    }

    // PENDING INITIALIZE
    if (privilege === "systemadmin" && pendingInitialize && dbEnabled && !dbInitialized) {
      if (dbModified) {
        return <Button type="primary" onClick={handleValidateDb} loading={dbValidationLoading} className="custom-button">Validate Database</Button>;
      }
      if (isAnyEmailBeingEdited) return null;
      return <Button type="primary" onClick={handleInitializeDb} loading={submitLoading} className="custom-button">Initialize</Button>;
    }

    // VIEW MODE
    if (mode === "view") {
      const enabled = orgDetails?.enabled || false;
      const initialized = orgDetails?.initialized || false;

      if (enabled && !initialized) {
        return <Button type="primary" onClick={handleInitializeDb} loading={submitLoading} className="custom-button">Initialize</Button>;
      }
      if (!enabled && orgDetails) {
        return <Button type="primary" onClick={handleEnableDb} loading={submitLoading} className="custom-button">Enable</Button>;
      }
    }

    // EDIT MODE
    if (mode === "edit") {
      if (dbModified) {
        return <Button type="primary" onClick={handleValidateDb} loading={dbValidationLoading} className="custom-button">Validate Database</Button>;
      } else if (!pendingInitialize) {
        if (!dbValidated || !activeEmailConfig || isAnyEmailBeingEdited) {
          return <Button onClick={handleCancel} className="custom-button" danger>Cancel</Button>;
        }
        return (
          <>
            <Button type="primary" onClick={handleSave} loading={submitLoading} className="custom-button">Save</Button>
            <Button onClick={handleCancel} className="custom-button" danger>Cancel</Button>
          </>
        );
      }
    }

    // ADD MODE
    if (privilege === "systemadmin" && mode === "add") {
      if (!dbValidated || dbModified) {
        return <Button type="primary" onClick={handleValidateDb} loading={dbValidationLoading} className="custom-button">Validate Database</Button>;
      }
      if (dbValidated && !dbAdded) {
        if (!activeEmailConfig || isAnyEmailBeingEdited) return null;
        return <Button type="primary" onClick={handleAddDb} loading={submitLoading} className="custom-button">Add</Button>;
      }
      if (dbAdded && !dbEnabled && !dbModified) {
        if (!activeEmailConfig || isAnyEmailBeingEdited) return null;
        return <Button type="primary" onClick={handleEnableDb} loading={submitLoading} className="custom-button">Enable</Button>;
      }
      if (dbEnabled && !dbInitialized) {
        if (dbModified) {
          return <Button type="primary" onClick={handleValidateDb} loading={dbValidationLoading} className="custom-button">Validate Database</Button>;
        }
        if (!activeEmailConfig || isAnyEmailBeingEdited) return null;
        return <Button type="primary" onClick={handleInitializeDb} loading={submitLoading} className="custom-button">Initialize</Button>;
      }
    }

    return null;
  };

  return (
    <div className="server-config-container">
      <Alert
        message="⚠️ These are critical configurations. Please proceed carefully. Changes take effect after restart."
        type="warning"
        className="server-alert"
      />

      <div className="server-config-card">
        <div className="server-config-header">Server Configuration</div>

        {mode === "view" && privilege === "systemadmin" && (
          <div className="server-config-edit-bar">
            <Button
              type="primary"
              onClick={() => {
                if (!dbEnabled && !dbInitialized) {
                  setPendingEnable(false); setPendingInitialize(false);
                } else if (dbEnabled && !dbInitialized) {
                  setPendingEnable(false); setPendingInitialize(true);
                } else {
                  setPendingEnable(false); setPendingInitialize(false);
                }
                // When entering edit mode, assume DB is validated already 
                setDbValidated(true);
                setMode("edit");
              }}
              className="custom-button"
            >
              Edit
            </Button>
          </div>
        )}

        {loading && (
          <Alert message="Fetching server configuration..." type="info" showIcon className="server-loading" />
        )}

        <Form layout="vertical">
          <div className="server-grid">
            {["org_uid", "user"].map((key) =>
              serverConfigFields[key] ? renderServerField(key, serverConfigFields[key]) : null,
            )}
            <Divider style={{ gridColumn: "1 / -1", color: "var(--text-color)", borderColor: "rgba(0,0,0,0.1)" }}>
              Database Configuration
            </Divider>
            {Object.entries(serverConfigFields)
              .filter(([key]) => key !== "org_uid" && key !== "user")
              .map(([key, cfg]) => renderServerField(key, cfg))}
          </div>

          {mode === "edit" && dbModified && (
            <Alert
              message="⚠️ Database configuration has been modified. Please re-validate the database connection to proceed."
              type="warning"
              className="db-modified-warning"
            />
          )}

          {renderEmailConfigSection()}

          <Space className="server-button-space">
            {renderDbActionButton()}
          </Space>
        </Form>
      </div>
    </div>
  );
};

export default ServerConfig;
